import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { authApi, formatApiError } from "../lib/api";
import { Camera, ArrowLeft, Loader2, Eye, EyeOff, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7] flex flex-col" data-testid="login-page">
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
              <Camera className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Festa.IA</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="bg-white border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl p-6 sm:p-8">
            <h1 className="text-2xl font-black text-[#1C1917] mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Entrar</h1>
            <p className="text-sm text-[#78716C] mb-6">Acesse sua conta de organizador</p>

            {error && (
              <div className="bg-[#FEE2E2] border-2 border-[#1C1917] rounded-lg p-3 mb-4 text-sm text-[#1C1917] font-medium" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-bold text-[#1C1917]">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="mt-1 border-2 border-[#1C1917] rounded-lg"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-bold text-[#1C1917]">Senha</Label>
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="flex items-center gap-1 text-xs font-bold text-[#FF6B6B] hover:underline"
                    data-testid="login-show-password-btn"
                  >
                    {showPassword ? (
                      <>
                        <EyeOff className="w-3.5 h-3.5" strokeWidth={2.5} /> Ocultar senha
                      </>
                    ) : (
                      <>
                        <Eye className="w-3.5 h-3.5" strokeWidth={2.5} /> Mostrar senha
                      </>
                    )}
                  </button>
                </div>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  required
                  className="mt-1 border-2 border-[#1C1917] rounded-lg"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all active:translate-y-[4px] active:shadow-none"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
              </Button>
            </form>

            <div className="text-center mt-3">
              <ForgotPasswordTrigger />
            </div>

            <p className="text-center text-sm text-[#78716C] mt-6">
              Nao tem conta?{" "}
              <Link to="/register" className="text-[#FF6B6B] font-bold hover:underline" data-testid="go-to-register">
                Criar conta
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


// Inline component: small "Esqueci senha?" link that opens a dialog where
// the user types their email. We always show a generic success message to
// prevent email enumeration; the backend silently no-ops on unknown emails.
function ForgotPasswordTrigger() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    setOpen(v);
    if (!v) {
      setEmail("");
      setSent(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-sm font-bold text-[#78716C] hover:text-[#FF6B6B] hover:underline"
          data-testid="forgot-password-btn"
        >
          Esqueci minha senha
        </button>
      </DialogTrigger>
      <DialogContent className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Esqueceu sua senha?
          </DialogTitle>
        </DialogHeader>
        {sent ? (
          <div className="space-y-3 text-center py-4">
            <div className="w-12 h-12 mx-auto bg-[#22C55E] border-2 border-[#1C1917] rounded-full flex items-center justify-center shadow-[3px_3px_0px_#1C1917]">
              <Mail className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <p className="text-sm text-[#1C1917] font-bold">Pronto! Verifique seu e-mail.</p>
            <p className="text-xs text-[#78716C] leading-snug">
              Se houver uma conta cadastrada com <span className="font-bold">{email}</span>, voce recebera um link
              para redefinir sua senha. O link vale por <span className="font-bold">1 hora</span>.
            </p>
            <Button
              onClick={() => close(false)}
              className="w-full bg-[#1C1917] text-white border-2 border-[#1C1917] rounded-full font-bold mt-2"
              data-testid="forgot-password-close-btn"
            >
              Fechar
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-xs text-[#78716C] leading-snug">
              Informe o e-mail da sua conta. Enviaremos um link para voce criar uma nova senha.
            </p>
            <div>
              <Label className="text-[11px] font-bold text-[#78716C] uppercase tracking-wider">E-mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="mt-1 border-2 border-[#1C1917] rounded-lg"
                data-testid="forgot-password-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sending || !email}
              className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all disabled:opacity-60"
              data-testid="forgot-password-submit-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enviar link de redefinicao
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
